import { decode, sign } from 'jsonwebtoken';
import { JWT_LOCAL_STORAGE_KEY } from '../consts/index';
import { ApolloError } from 'apollo-client';
import { AdminUser, SelectObject } from './types';
import * as moment from 'moment';
import { Recurs, VENDOR_ROLES, SECURITY_ROLES, USER_ROLES } from './enums';
import { UserRole, Product } from '../generated/graphql';

import * as XLSX from 'xlsx';

/**
 * Helper function to convert an object into an array.
 * @param object - the object to be converted into an array.
 */
export const getArrayFromObject = (object: any) => {
  let arrayFromObject: any[] = [];

  if (object && typeof object === 'object') {
    Object.keys(object).forEach(key => {
      arrayFromObject.push({ accessor: key, value: object[key] });
    });
  }

  return arrayFromObject;
};
type LatLong = {
  lat: number;
  lng: number;
}
/**
 * Helper function to convert camelCase strings into regular
 * capitalized strings.
 * @param word - camelCase word to be converted to capitalized word.
 */
export function toCapitalizedWords(word: string): string {
  var words = word.match(/[A-Za-z][a-z]*/g) || [];

  return words.map(capitalize).join(' ');
}

/**
 * Helper function to capitalize the first letter of a string.
 * @param word - word to have it's first letter capitalized.
 */
function capitalize(word: string): string {
  return word.charAt(0).toUpperCase() + word.substring(1);
}

/**
 * Helper function to Return a string from a LatLong object for the Leaflet Component.
 * @param latlong - LatLng object to transpose to a string
 */
export function getStringFromLatLng(latlong: LatLong): string {
  return latlong.lng.toFixed(4) + ',' + latlong.lat.toFixed(4);
}

/**
 * This function will format a string from an enum,
 * making only the first letter a capital and making
 * the rest lower case, it also removes underscores
 * and replacing them with spaces.
 * @param {string} toFix - The string we are wanting
 * to format.
 * @param {boolean} allFirstCaps - If true, all words
 * in the string will get a capital first letter.
 */
export function formatString(toFix: string, firstLetterCaps: boolean, isUrl?: boolean): string {
  try {
    if (firstLetterCaps) {
      let sentence = '';
      const temp = toFix
        .toLowerCase()
        .replace(/_/g, ' ')
        .split(' ');
      temp.forEach(word => (sentence += word.charAt(0).toUpperCase() + word.substr(1).toLowerCase() + ' '));
      return sentence;
    }
    if (isUrl) {
      const temp = toFix.toLowerCase().replace(' ', '-');
      return temp;
    }
    return (
      toFix.charAt(0).toUpperCase() +
      toFix
        .substr(1)
        .toLowerCase()
        .replace(/_/g, ' ')
    );
  } catch (error) {
    return toFix;
  }
}

/**
 * This function serves to strip the value property/properties
 * out of the form's Select component.
 * @param {object} data - This is the object containing
 * all the select values that we'd like to clean up.
 */
export function formatFormValues(data: object): object {
  try {
    const dataCopy = JSON.parse(JSON.stringify(data));
    Object.keys(dataCopy).forEach((key: any) => {
      const val = dataCopy[key];
      if (val && Array.isArray(val) && val.length > 0) {
        // TODO: Find a way to better distinguish between multi select arrays & other arrays
        if (typeof dataCopy[key][0] === 'object' && 'isMultiSelect' in dataCopy[key][0]) {
          const enumsArr = dataCopy[key].map((obj: any) => {
            if (typeof obj === 'object' && 'label' in obj && 'value' in obj) {
              return obj.value;
            }
          });
          dataCopy[key] = enumsArr;
        }
      } else if (val && typeof val === 'object' && 'label' in val && 'value' in val) {
        const selectValue = val.value;
        dataCopy[key] = selectValue;
      }
    });
    return dataCopy;
  } catch (error) {
    return {};
  }
}

/**
 * Get the corresponding SelectObject for the value provided.
 * @param value - The value to get the select object of
 * @param options - Select Objects to find from
 */
export const formatToSelectValue = (value: string, options: SelectObject[]): any => {
  const index = options.map(opt => opt.value).findIndex(val => val === value);
  return index === -1 ? '' : options[index];
}

/**
 * This function serves to mutate an enum value which is
 * being returned from the backend into an object that's compatible
 * with react-select.
 * @param { string | { label: string; value: string } } data - the variable
 * to either be returned if it's already a select object,
 * or to be made into one if it isn't.
 */
export function formatSelectValue(
  data: string | { label: string; value: string; isMultiSelect?: boolean } | null
): { label: string; value: string } | [{ label: string; value: string; isMultiSelect?: boolean }] | string | null {
  try {
    if (data && Array.isArray(data)) {
      const array: any = [];
      data.map(item => {
        array.push({
          label: formatString(item, true),
          value: item,
          isMultiSelect: true,
        });
      });
      return array;
    } else if (data && typeof data !== 'object') {
      return {
        label: formatString(data, true),
        value: data,
      };
    } else if (data && typeof data === 'object' && 'label' in data && 'value' in data) {
      return data;
    }
    return null;
  } catch (error) {
    return null;
  }
}

export const ValidateCharacterLimit = (value: string) => {
  let errormessage;
  if (value.length > 1024) {
    errormessage = 'Over character limit';
  }
  return errormessage;
}

/**
 * This function serves to mutate a data array value which is
 * being returned from the backend into an object that's compatible
 * with react-select, with the value specifically being the items ID.
 * @param { SelectObject[] | { label: string; options: SelectObject[] }[] | null } - the variable
 * to either be returned if it's already a select object,
 * or to be made into one if it isn't.
 */
export function createSelectOptions(
  data: Array<{}> | null
): SelectObject[] | { label: string; options: SelectObject[] }[] | undefined {
  if (data) {
    return data.map((item: any) => ({
      label: item.name,
      value: item.id,
    }));
  }
  return undefined;
}

/**
 * This function serves to combine the date from StyledDatePicker with
 * the time from StyledTimePicker
 * @param { }
 */
export function formatDateTime(date: moment.Moment, time: moment.Moment): moment.Moment | undefined {
  if (date && time) {
    try {
      const dateWithoutTime = moment(date).format('YYYY-MM-DD');
      const formattedTime = moment(time).format('HH:mm');
      return moment(dateWithoutTime + ' ' + formattedTime);
    } catch (error) {
      return undefined;
    }
  }
  return undefined;
}

/**
 * This function serves to format the time from StyledTimePicker
 * @param { }
 */
export function formatTime(time: any): moment.Moment | undefined {
  if ( time) {
    try {
      const formattedTime = moment(time).format('h:mm:ss');
      return moment(formattedTime);
    } catch (error) {
      return undefined;
    }
  }
  return undefined;
}

/**
 * This function serves to strip the +27 from the phone number
 * for initial value purposes.
 * @param { string } cell - the cellphone number to edit.
 */
export function formatCellNumber(cell: string): string | null {
  try {
    return cell.replace('+27', '');
  } catch (error) {
    return null;
  }
}

/**
 * Checks if a token has not expired
 * @param token
 */
export function isTokenValid(token: string | null): boolean {
  if (!token) {
    return false;
  }
  try {
    const decodedJwt: any = decode(token);
    return decodedJwt.exp >= Date.now() / 1000;
  } catch (e) {
    return false;
  }
}

/**
 * Checks if a user is logged in
 */
export function isLoggedIn(): boolean {
  return isTokenValid(window.localStorage.getItem(JWT_LOCAL_STORAGE_KEY));
}

/**
 * Helper function to validate user permissions
 */
export function currentUserPermissionCheck(permissions: UserRole[]): boolean {
  const user = getLoggedInUser();
  if (user && user.role) {
    return permissions.includes(user.role);
  }
  return false;
}

/**
 *  Helper Function to Calcualted the distance between to points
 * * @param {number} lat1 - latitude of the first point
 * * @param {number} lng1 - longitude of the first point
 * * @param {number} lat2 - latitude of the second point
 * * @param {number} lng2 - longitude of the first point
 * 
 */
export function distanceBetweenTwoPoints(lat1: number , lng1: number , lat2: number , lng2: number): number {
   const radLat1 = Math.PI * lat1 / 180 ;
   const radLat2 = Math.PI * lat2 / 180 
   const theta = lng1 - lng2;
   const radtheta = Math.PI * theta / 180
   let distance = Math.sin(radLat1) * Math.sin(radLat2) + Math.cos(radLat1) *
   Math.cos(radLat2) * Math.cos(radtheta);
   distance = Math.acos(distance);
   distance = distance * 180 / Math.PI;
   distance = distance * 60 / 1.1515;
   distance = (distance * 1.609344) * 1000;
   return distance
}

/**
 * Function that returns the user profile
 * of the current logged in user
 */
export function getLoggedInUser(): AdminUser {
  try {
    const token: any = window.localStorage.getItem(JWT_LOCAL_STORAGE_KEY);
    const decodedJwt: any = decode(token);
    return decodedJwt.user;
  } catch (error) {
    return null;
  }
}

export function generatePrismaToken(): string {
  return `Bearer ${sign(
    {
      data: {
        service: 'default@default',
      },
    },
    // @ts-ignore
    'P3JF46efXeU9fGj',
    // process.env.REACT_APP_PRISMA_MANAGEMENT_API_SECRET,
    {
      expiresIn: '24h',
    }
  )}`;
}

/**
 * Return user role options based on the users role
 * @param {AdminUser} user - The currently logged in user
 * @returns {SelectObject[]}
 */
export const getUserRoles = (user: AdminUser): SelectObject[] => {
  if (user) {
    if ([UserRole.VendorAdmin, UserRole.VendorUser].includes(user.role)) {
      return VENDOR_ROLES;
    }
    if ([UserRole.SecurityAdmin].includes(user.role)) {
      return SECURITY_ROLES;
    }
    return USER_ROLES;
  }
  return [];
}

/**
 * Formats the error that comes back from the
 * server into a more user friendly string.
 * @param error - the error from the GraphQL server
 * to be updated.
 */
export function formatGqlError(error: ApolloError) {
  const graphQLErrorMessage = error.graphQLErrors && error.graphQLErrors[0] && error.graphQLErrors[0].message;
  return (graphQLErrorMessage || error.message).replace('GraphQL error:', '').trim();
}

/**
 * Function that returns an inputs label
 * with it's first letter capitalized
 */
export function getCapitalizedLabel(label: string) {
  switch (label) {
    case 'id':
      return 'Unique ID';
    default:
      return toCapitalizedWords(label);
  }
}

/**
 * Function that served to convert an uploaded file
 * to base64
 */
export const fileToBase64 = (file: any): Promise<string> =>
  new Promise(function (resolve: any, reject: any) {
    var reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = function () {
      resolve(reader.result);
    };
    reader.onerror = function (error: any) {
      reject(error);
    };
  });

/**
 * Custom logger which only outputs logs when NODE_ENV !== production
 */
export function logger(message?: any, ...optionalParams: any[]): void {
  if (process.env.NODE_ENV !== 'production') {
    console.log(message, ...optionalParams);
  }
}

/**
 * return an array of dates given a start date,
 * end date and a recurring frequency
 * @param startDate - first instance of the event
 * @param endDate - last instance of the event
 * @param frequency - interval between each instance
 */
export function calculateRecurringDates(startDate: moment.Moment, endDate?: moment.Moment, frequency?: Recurs) {
  let currentDate = moment(startDate);
  const interval = moment(endDate).diff(startDate, frequency);
  currentDate.add(1, frequency);
  const recurs = [currentDate.toDate()];
  for (let index = 1; index < interval; index++) {
    currentDate.add(1, frequency);
    recurs.push(currentDate.toDate());
  }
  return recurs;
}

export const unwrap = (obj: any, fields: string[] = []): any => {
  if (obj) {
    if (fields.length === 1) {
      if (fields[0] in obj) {
        return obj[fields[0]];
      }
    }
    if (fields[0] in obj) {
      return unwrap(obj[fields[0]], fields.slice(1));
    }
  }
  return undefined;
}

/**
 * Download an array of strings as CSV
 * @param {string[]} data - The data to download
 * @param {string} downloadName - The name of the download
 */
export const downloadCSV = (data: Array<string[]>, downloadName: string): void => {
  let csv = '';
  data.forEach((row: string[]) => {
    csv += row.join(',');
    csv += '\n';
  });
  const hiddenElement = document.createElement('a');
  hiddenElement.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv);
  hiddenElement.target = '_blank';
  hiddenElement.download = `${downloadName}${moment().format('YYYYMMDD')}.csv`;
  hiddenElement.click();
}

/**
 * Download an array of strings as XLXS
 * @param {string[]} data - The data to download
 * @param {string} downloadName - The name of the download
 */
export const downloadXLSX = (data: Array<string[]>, downloadName: string) => {
  const wb = XLSX.utils.book_new(),
    ws = XLSX.utils.aoa_to_sheet(data);
  XLSX.utils.book_append_sheet(wb, ws, `${downloadName}${moment().format('YYYYMMDD')}`);
  XLSX.writeFile(wb, `${downloadName}${moment().format('YYYYMMDD')}.xlsx`);
};

/**
 * Helper function to calculate the total remaning capacity of
 * an RSVP event.
 * @param products - array of products for that specific event.
 * @param capacity - total capacity for given product.
 */
export function calcRsvpCapacity(products: Product[], capacity: number): number {
  const filteredProducts = products.filter(product => product.name !== 'NO');
  const rsvpCapacity = filteredProducts.reduce((total: any, current: any) => total + current.capacity, 0);
  const rsvpTickets = filteredProducts.reduce((total: any, current: any) => total + current.tickets.length, 0);
  // capacity is passed down from the tables cell value, this fixes an issue where the capacity doesn't
  // display correctly on the initial load
  if (rsvpTickets === 0) {
    return capacity;
  } else if (products && products.length > 0) {
    // When a user RSVP's as NO, the rsvp is still considered a ticket. Here we filter out those tickets as
    // they obscure the total capacity results
    return rsvpCapacity - rsvpTickets;
  }
  return products && products.length;
}

/**
 * Helper function to calculate the total remaning capacity of
 * a non RSVP event.
 * @param products - array of products for that specific event.
 * @param index - products array item index.
 */
export function calcTicketCapacity(products: Product[], index: number) {
  const productInfo = products.map((product: any) => product.tickets.length);
  return productInfo && (productInfo[index] || 0);
}

/**
 * helper function that takes in any number of minutes,
 * and returns the a moment object as hours and minutes
 * @param seconds - number of minutes
 */
export function formatDuration(seconds: number) {
  return moment
    .utc()
    .startOf('day')
    .add({ seconds });
}

/**
 * helper function that takes in data with a name and an ID,
 * and returns the an object with a label containing the name,
 * and a value containing the ID
 * @param values - data from the API
 */
export function createSelectOptionsWithId(values: any, isCellNumber?: boolean) {
  try {
    if (values && Array.isArray(values)) {
      return values.map((item: any) => ({
        label: formatString(item.name || item.firstName + ' ' + item.lastName, true),
        value: isCellNumber ? item.cellNumber : item.id,
        isMultiSelect: true,
      }));
    } else if (values && typeof values === 'object') {
      return {
        label: formatString(values.name || values.firstName + ' ' + values.lastName, true),
        value: isCellNumber ? values.cellNumber : values.id,
      };
    } else if (values && typeof values === 'object' && 'label' in values && 'value' in values) {
      return values;
    }
    return null;
  } catch (error) {
    return null;
  }
}


// WEBPACK FOOTER //
// ./src/utils/helpers.ts